<template>
    <div v-bind="$attrs" class="pb-10">
        <button class="standard-link text-lg sm:text-xl" @click="catalogExpanded = !catalogExpanded">
            <i class="fa-solid fa-angle-right mr-1 transition-transform" :class="{ 'rotate-90': catalogExpanded }"></i>
            Browse our full catalog
        </button>

        <!-- Filter options -->
        <div class="mt-2 flex flex-wrap items-center gap-2 md:mt-4 md:gap-3" v-if="catalogExpanded">
            <FormInput
                id="name-search"
                type="text"
                maxlength="100"
                placeholder="Search by name…"
                class="my-1 max-md:w-full md:flex-1"
                v-model="searchTerm"
            />
            <Multiselect
                ref="countrySelect"
                class="max-sm:w-full sm:max-md:flex-1 md:w-48 lg:w-80 xl:w-96"
                :searchable="true"
                :allow-empty="true"
                placeholder="Country"
                v-model="searchCountry"
                :options="searchCountry ? countryOptionsWithClear : countryOptions"
                :show-labels="false"
            >
                <template #singleLabel="{ option }">
                    <span class="line-clamp-1 h-8">{{ option }}</span>
                </template>
            </Multiselect>
            <Multiselect
                ref="currencySelect"
                class="max-sm:w-full sm:max-md:flex-1 md:w-36"
                :searchable="true"
                :allow-empty="true"
                placeholder="Currency"
                v-model="searchCurrency"
                :options="searchCurrency ? currencyOptionsWithClear : currencyOptions"
                :show-labels="false"
            >
                <template #singleLabel="{ option }">
                    <span class="line-clamp-1 h-8">{{ option }}</span>
                </template>
            </Multiselect>
        </div>

        <div v-if="filteredOptions.length === 0" class="mt-4 flex justify-center text-center sm:mt-2">
            <p class="text-lg text-dark-gray">No gift cards found matching your search.</p>
            <!-- TODO Add search suggestions -->
        </div>

        <table class="w-full table-auto max-md:mt-4" v-show="catalogExpanded && filteredOptions.length > 0">
            <thead class="sticky top-0 bg-white text-left text-xl text-purple max-md:hidden">
                <tr class="bg-light-gray">
                    <th class="h-4"></th>
                    <th class="h-4"></th>
                    <th class="h-4"></th>
                </tr>
                <tr>
                    <th class="rounded-tl-lg py-3 pl-4">Name</th>
                    <th>Country</th>
                    <th class="rounded-tr-lg pr-4">Currency</th>
                </tr>
            </thead>
            <tbody>
                <a
                    v-for="(giftCardOption, index) in filteredOptions"
                    :key="giftCardOption.name + giftCardOption.country_iso"
                    class="entry-row"
                    :class="{ 'bg-white': index % 2 !== 0 }"
                    :href="`/gift-cards/${giftCardOption.slug}`"
                >
                    <div class="option-name" :class="{ 'rounded-l-md': index % 2 !== 0 }">
                        <span>{{ giftCardOption.name }}</span>
                        <span class="mt-1 block text-regular-gray md:hidden">{{ giftCardOption.country }}</span>
                    </div>
                    <span class="option-country">
                        {{ giftCardOption.country }}
                    </span>
                    <span class="option-currency" :class="{ 'rounded-r-md': index % 2 !== 0 }">
                        {{ giftCardOption.currency }}
                    </span>
                </a>
            </tbody>
        </table>
    </div>
</template>
<script lang="ts" setup>
import Multiselect from 'vue-multiselect'
import { computed, onMounted, ref, watch } from 'vue'
import FormInput from '@/components/FormInput.vue'
import { useDebounceFn } from '@vueuse/core'

interface GiftCardOption {
    name: string
    country_iso: string
    country: string
    currency: string
    slug: string
}

const props = defineProps<{
    giftCardOptions: GiftCardOption[]
}>()

const filteredOptions = ref(props.giftCardOptions)
const countryOptions = computed(() => Array.from(new Set(props.giftCardOptions.map(option => option.country))).sort())
const currencyOptions = computed(() => Array.from(new Set(props.giftCardOptions.map(option => option.currency))).sort())

const countryOptionsWithClear = computed(() => ['Clear selection', ...countryOptions.value])
const currencyOptionsWithClear = computed(() => ['Clear selection', ...currencyOptions.value])

const catalogExpanded = ref(sessionStorage?.getItem('gift_catalog_expanded') === 'true')

const searchTerm = ref(sessionStorage?.getItem('gift_catalog_search_term') || '')
const searchCountry = ref(sessionStorage?.getItem('gift_catalog_search_country') || '')
const searchCurrency = ref(sessionStorage?.getItem('gift_catalog_search_currency') || '')

const filter = () => {
    filteredOptions.value = props.giftCardOptions

    if (!searchTerm.value && !searchCountry.value && !searchCurrency.value) {
        return
    }

    const country = searchCountry.value

    if (country) {
        filteredOptions.value = filteredOptions.value.filter(option => option.country === country)
    }

    const currency = searchCurrency.value

    if (currency) {
        filteredOptions.value = filteredOptions.value.filter(option => option.currency === currency)
    }

    const term = searchTerm.value.toLowerCase()
    if (term) {
        filteredOptions.value = filteredOptions.value.filter(
            option =>
                option.country.toLowerCase().includes(term) ||
                option.currency.toLowerCase().includes(term) ||
                option.name.toLowerCase().includes(term),
        )
    }
}

const debounceFilter = useDebounceFn(filter, 200)

watch(catalogExpanded, () => {
    sessionStorage?.setItem('gift_catalog_expanded', catalogExpanded.value ? 'true' : 'false')
})
watch(searchTerm, () => {
    sessionStorage?.setItem('gift_catalog_search_term', searchTerm.value)
    debounceFilter()
})

watch(searchCountry, () => {
    if (searchCountry.value === 'Clear selection') {
        searchCountry.value = ''
    }
    sessionStorage?.setItem('gift_catalog_search_country', searchCountry.value)
    debounceFilter()
})

watch(searchCurrency, () => {
    if (searchCurrency.value === 'Clear selection') {
        searchCurrency.value = ''
    }
    sessionStorage?.setItem('gift_catalog_search_currency', searchCurrency.value)
    debounceFilter()
})

onMounted(() => {
    if (window.location.hash === '#catalog') {
        catalogExpanded.value = true
    }
})
</script>
<style scoped>
.entry-row {
    @apply table-row text-dark-gray sm:text-lg;
    @apply no-underline !important;
}

.option-name {
    @apply table-cell py-1.5 px-2 font-semibold sm:pl-4;
}

.option-country {
    @apply hidden w-48 pr-2 md:table-cell lg:w-80 xl:w-96;
}

.option-currency {
    @apply table-cell w-36 py-1.5 pr-2 max-md:pl-2 max-md:text-right max-md:align-bottom max-md:text-regular-gray sm:pr-4;
}

.entry-row * {
    @apply transition-colors;
}

.entry-row:hover * {
    @apply text-green;
}
</style>
